import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'react-hot-toast';
import { Search, Plus, Edit2, Trash2, User, Building2, CreditCard, Banknote, MapPin, Truck, X, ChevronUp, Phone } from 'lucide-react';
import BrandSearchDropdown from '@/Components/shared/BrandSearchDropdown';

export default function PurchasingSupplierDatabase() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  React.useEffect(() => { setCurrentPage(1); }, [search]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [form, setForm] = useState({
    nama_sales: '',
    bank: '',
    no_rekening: '',
    atas_nama: '',
    metode_pembayaran: 'Cash',
    ongkir_mode: 'transfer',
    ppn_status: 'include',
    npwp: '',
    ada_faktur_pajak: 'Tidak',
    phone: '',
    alamat: '',
    handled_vendors: []
  });

  const [vendorInput, setVendorInput] = useState('');

  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: ['supplier-database'],
    queryFn: async () => {
      // In case the table isn't created yet or RPC failed, we use a fallback array
      try {
        const res = await base44.entities.SupplierDatabase?.list();
        return res || [];
      } catch (err) {
        console.warn("Table SupplierDatabase might not exist yet", err);
        return [];
      }
    }
  });
  const { data: dbBrandsRaw = [] } = useQuery({ queryKey: ['racikan-database'], queryFn: () => base44.entities.RacikanDatabase?.list().catch(() => []) });
  const { data: dbBahanRaw = [] } = useQuery({ queryKey: ['database-bahan'], queryFn: () => base44.entities.DatabaseBahan?.list().catch(() => []) });
  const { data: inventoryItems = [] } = useQuery({ queryKey: ['inventory'], queryFn: () => base44.entities.InventoryItem?.list().catch(() => []) });

  // Stok & Inventori tables (where Expression, Parfarome, TFi etc. live)
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol?.list().catch(() => []) });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup?.list().catch(() => []) });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray?.list().catch(() => []) });
  const { data: fullsetList = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset?.list().catch(() => []) });
  const { data: bahanCairList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair?.list().catch(() => []) });
  const { data: itemTambahanList = [] } = useQuery({ queryKey: ['item-tambahan'], queryFn: () => base44.entities.ItemTambahan?.list().catch(() => []) });

  const allAvailableVendors = useMemo(() => {
    const vendors = new Set();
    
    // Add vendors from existing suppliers
    suppliers.forEach(s => {
      if (s.handled_vendors && Array.isArray(s.handled_vendors)) {
        s.handled_vendors.forEach(v => vendors.add(v));
      }
    });

    // Add vendors from Stok & Inventori (BahanCair, Botol, Tutup, Spray, Fullset, ItemTambahan)
    bahanCairList.forEach(b => { if (b.vendor) vendors.add(b.vendor); });
    botolList.forEach(b => { if (b.vendor) vendors.add(b.vendor); });
    tutupList.forEach(b => { if (b.vendor) vendors.add(b.vendor); });
    sprayList.forEach(b => { if (b.vendor) vendors.add(b.vendor); });
    fullsetList.forEach(b => { if (b.vendor) vendors.add(b.vendor); });
    itemTambahanList.forEach(b => { if (b.vendor) vendors.add(b.vendor); });

    // Also check older DB tables
    dbBrandsRaw.forEach(b => { if (b.vendor) vendors.add(b.vendor); });
    dbBahanRaw.forEach(b => { if (b.vendor) vendors.add(b.vendor); });
    inventoryItems.forEach(i => { if (i.vendor) vendors.add(i.vendor); });

    return Array.from(vendors).filter(Boolean).sort();
  }, [suppliers, bahanCairList, botolList, tutupList, sprayList, fullsetList, itemTambahanList, dbBrandsRaw, dbBahanRaw, inventoryItems]);
  const createMut = useMutation({
    mutationFn: (data) => base44.entities.SupplierDatabase.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-database'] });
      toast.success('Supplier berhasil ditambahkan');
      setIsModalOpen(false);
    },
    onError: (err) => toast.error('Gagal menambah supplier: ' + err.message)
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => base44.entities.SupplierDatabase.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-database'] });
      toast.success('Supplier berhasil diupdate');
      setIsModalOpen(false);
    },
    onError: (err) => toast.error('Gagal update supplier: ' + err.message)
  });

  const deleteMut = useMutation({
    mutationFn: (id) => base44.entities.SupplierDatabase.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-database'] });
      toast.success('Supplier berhasil dihapus');
    },
    onError: (err) => toast.error('Gagal hapus supplier: ' + err.message)
  });

  const filtered = useMemo(() => {
    if (!search) return suppliers;
    const q = search.toLowerCase();
    return suppliers.filter(s => 
      (s.nama_sales || '').toLowerCase().includes(q) ||
      (s.atas_nama || '').toLowerCase().includes(q) ||
      (s.bank || '').toLowerCase().includes(q)
    );
  }, [suppliers, search]);

  const handleOpenAdd = () => {
    setEditingId(null);
    setForm({
      nama_sales: '',
      bank: '',
      no_rekening: '',
      atas_nama: '',
      metode_pembayaran: 'Cash',
      ongkir_mode: 'transfer',
      ppn_status: 'include',
      npwp: '',
      phone: '',
      alamat: '',
      handled_vendors: []
    });
    setVendorInput('');
    setIsModalOpen(true);
  };

  const handleOpenEdit = (sup) => {
    setEditingId(sup.id);
    setForm({
      nama_sales: sup.nama_sales || '',
      bank: sup.bank || '',
      no_rekening: sup.no_rekening || '',
      atas_nama: sup.atas_nama || '',
      metode_pembayaran: sup.metode_pembayaran || 'Cash',
      ongkir_mode: sup.ongkir_mode || 'transfer',
      ppn_status: sup.ppn_status || 'include',
      npwp: sup.npwp || '',
      phone: sup.phone || sup.no_telp || sup.no_telepon || '',
      alamat: sup.alamat || sup.address || '',
      handled_vendors: sup.handled_vendors || []
    });
    setVendorInput('');
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    if (confirm('Yakin ingin menghapus supplier ini?')) {
      deleteMut.mutate(id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nama_sales.trim()) return toast.error('Nama Vendor/Sales wajib diisi');
    
    if (editingId) {
      updateMut.mutate({ id: editingId, data: form });
    } else {
      createMut.mutate(form);
    }
  };

  return (
    <div className="space-y-6 text-[#302621]">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-800">Database Supplier / Sales</h1>
          <p className="text-xs text-slate-500 mt-1">
            Kelola data vendor, rekening pembayaran, dan preferensi metode pembayaran untuk pengajuan dana.
          </p>
        </div>
        <Button 
          onClick={handleOpenAdd}
          className="bg-[#8C6208] text-white hover:bg-[#724F06] gap-2 text-xs font-bold rounded-xl h-10 shadow-sm"
        >
          <Plus className="w-4 h-4" /> Tambah Supplier
        </Button>
      </div>

      <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-100">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cari nama sales, bank, pemilik rek..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9 text-xs"
            />
          </div>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {isLoading ? (
            <div className="col-span-full py-12 text-center text-sm text-slate-400">Memuat data supplier...</div>
          ) : filtered.length === 0 ? (
            <div className="col-span-full py-12 text-center text-sm text-slate-400">Belum ada data supplier.</div>
          ) : (
            filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(sup => (
              <Card key={sup.id} className="border border-slate-100 shadow-xs hover:shadow-md transition-shadow">
                <CardContent className="p-4 relative group">
                  <div className="absolute top-4 right-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-blue-600" onClick={() => handleOpenEdit(sup)}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-rose-600" onClick={() => handleDelete(sup.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0 border border-amber-100">
                      <Building2 className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm text-slate-800">{sup.nama_sales}</h3>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">
                          <Banknote className="w-3 h-3" /> {sup.metode_pembayaran || 'Cash'}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-blue-50 px-1.5 py-0.5 rounded text-blue-700">
                          <Truck className="w-3 h-3" /> {sup.ongkir_mode === 'free_ongkir' ? 'Free Ongkir' : (sup.ongkir_mode === 'petty_cash' ? 'Petty Cash' : 'Transfer')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
                    <div className="flex items-start gap-2 text-xs">
                      <CreditCard className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">{sup.bank || '-'}</p>
                        <p className="font-mono text-slate-700 font-semibold">{sup.no_rekening || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 text-xs">
                      <User className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">Atas Nama</p>
                        <p className="font-medium text-slate-700 truncate">{sup.atas_nama || '-'}</p>
                      </div>
                    </div>
                    {sup.phone && (
                      <div className="flex items-start gap-2 text-xs">
                        <Phone className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">No. Telp</p>
                          <p className="font-medium text-slate-700 truncate">{sup.phone}</p>
                        </div>
                      </div>
                    )}
                    {sup.alamat && (
                      <div className="flex items-start gap-2 text-xs">
                        <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">Alamat</p>
                          <p className="font-medium text-slate-700 text-[10px] leading-tight line-clamp-2">{sup.alamat}</p>
                        </div>
                      </div>
                    )}
                    {sup.handled_vendors && sup.handled_vendors.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-slate-50">
                        <p className="text-[9px] uppercase font-bold text-slate-400 mb-1">Menaungi Vendor:</p>
                        <div className="flex flex-wrap gap-1">
                          {sup.handled_vendors.map((v, i) => (
                            <span key={i} className="inline-flex items-center text-[9px] border border-slate-200 bg-white text-slate-600 rounded px-1.5 py-0.5">
                              {v}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
          
          {/* ── Pagination Controls ── */}
          {filtered.length > 0 && (
            <div className="col-span-full px-6 py-4 border border-slate-100 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
                <div className="w-[80px]">
                  <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                    <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 25, 50, 100].map(n => (
                        <SelectItem key={n} value={String(n)} className="text-[11px] font-bold">{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &lt;
                </button>
                
                {(() => {
                  const totalPages = Math.ceil(filtered.length / itemsPerPage);
                  const pages = [];
                  for(let i=1; i<=totalPages; i++) {
                    if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-black transition-all ${
                            currentPage === i 
                              ? 'bg-[#8C6208] text-white shadow-md shadow-[#8C6208]/30' 
                              : 'text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    } else if (i === currentPage - 2 || i === currentPage + 2) {
                      pages.push(<span key={i} className="text-slate-400 text-[10px] px-1">...</span>);
                    }
                  }
                  return pages;
                })()}
                
                <button 
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(filtered.length / itemsPerPage), p + 1))}
                  disabled={currentPage >= Math.ceil(filtered.length / itemsPerPage)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Supplier' : 'Tambah Supplier Baru'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 text-xs mt-2">
            <div className="space-y-1.5">
              <Label className="font-bold uppercase text-[10px] text-slate-500">Nama Vendor / Sales <span className="text-rose-500">*</span></Label>
              <Input 
                value={form.nama_sales} 
                onChange={e => setForm({...form, nama_sales: e.target.value})} 
                placeholder="Contoh: PT. Bintang Jaya / Budi Sales"
                autoFocus
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="font-bold uppercase text-[10px] text-slate-500">No. Telepon / WhatsApp</Label>
                <Input 
                  value={form.phone || ''} 
                  onChange={e => setForm(prev => ({...prev, phone: e.target.value}))} 
                  placeholder="Contoh: 08123456789"
                />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="font-bold uppercase text-[10px] text-slate-500">Alamat Lengkap</Label>
                <Textarea 
                  value={form.alamat || ''} 
                  onChange={e => setForm(prev => ({...prev, alamat: e.target.value}))} 
                  placeholder="Jl. Sudirman No. 123..."
                  className="min-h-[80px] resize-y"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="font-bold uppercase text-[10px] text-slate-500">Vendor / Brand yang Dinaungi</Label>
              <div className="flex gap-2">
                <div className="flex-1 relative z-50">
                  <BrandSearchDropdown 
                    brands={allAvailableVendors}
                    value={vendorInput}
                    onChange={setVendorInput}
                    onSelect={(brand) => {
                      if (brand.trim() && !form.handled_vendors?.includes(brand.trim())) {
                        setForm(prev => ({...prev, handled_vendors: [...(prev.handled_vendors || []), brand.trim()]}));
                      }
                      setVendorInput('');
                    }}
                    placeholder="Cari dan pilih vendor/brand..."
                  />
                </div>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (vendorInput.trim() && !form.handled_vendors?.includes(vendorInput.trim())) {
                      setForm(prev => ({...prev, handled_vendors: [...(prev.handled_vendors || []), vendorInput.trim()]}));
                      setVendorInput('');
                    }
                  }}
                  className="h-[54px] px-3 shrink-0 rounded-xl border-[#8C6208]/30 hover:bg-[#8C6208] hover:text-white"
                  title="Tambahkan Vendor Baru"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {form.handled_vendors && form.handled_vendors.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2 p-2 bg-slate-50 rounded-lg border border-slate-100 min-h-[36px]">
                  {form.handled_vendors.map((v, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-[#F9F5EC] border border-[#E6D5B8] text-xs font-medium px-2.5 py-1.5 rounded-lg text-[#8C6208] shadow-sm">
                      {v}
                      <button 
                        type="button" 
                        onClick={() => setForm(prev => ({...prev, handled_vendors: prev.handled_vendors.filter((_, idx) => idx !== i)}))}
                        className="hover:text-red-500 hover:bg-red-50 rounded p-0.5 ml-1 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="font-bold uppercase text-[10px] text-slate-500">Bank Tujuan</Label>
                <Input 
                  value={form.bank} 
                  onChange={e => setForm({...form, bank: e.target.value})} 
                  placeholder="BCA, Mandiri, dll"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold uppercase text-[10px] text-slate-500">Nomor Rekening</Label>
                <Input 
                  value={form.no_rekening} 
                  onChange={e => setForm({...form, no_rekening: e.target.value})} 
                  placeholder="0123456789"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="font-bold uppercase text-[10px] text-slate-500">Atas Nama Rekening</Label>
              <Input 
                value={form.atas_nama} 
                onChange={e => setForm({...form, atas_nama: e.target.value})} 
                placeholder="Nama Pemilik Rekening"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="font-bold uppercase text-[10px] text-slate-500">Metode Pembayaran</Label>
                <Select value={form.metode_pembayaran} onValueChange={v => setForm({...form, metode_pembayaran: v})}>
                  <SelectTrigger className="bg-white"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-white z-[9999]">
                    <SelectItem value="Cash">Cash (Bayar di Muka)</SelectItem>
                    <SelectItem value="Tempo">Tempo (Hutang)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold uppercase text-[10px] text-slate-500">Ongkir Default</Label>
                <Select value={form.ongkir_mode} onValueChange={v => setForm({...form, ongkir_mode: v})}>
                  <SelectTrigger className="bg-white"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-white z-[9999]">
                    <SelectItem value="transfer">Transfer Bank</SelectItem>
                    <SelectItem value="petty_cash">Pakai Petty Cash</SelectItem>
                    <SelectItem value="free_ongkir">Free Ongkir</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold uppercase text-[10px] text-slate-500">Status PPN</Label>
                <Select value={form.ppn_status} onValueChange={v => setForm({...form, ppn_status: v})}>
                  <SelectTrigger className="bg-white"><SelectValue/></SelectTrigger>
                  <SelectContent className="bg-white z-[9999]">
                    <SelectItem value="include">Include PPN</SelectItem>
                    <SelectItem value="exclude">Exclude PPN</SelectItem>
                  </SelectContent>
                </Select>
            </div>
            
            <div className="space-y-1.5">
              <Label className="font-bold uppercase text-[10px] text-slate-500">Ada Faktur Pajak?</Label>
              <Select value={form.ada_faktur_pajak || 'Tidak'} onValueChange={v => setForm({...form, ada_faktur_pajak: v})}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="Pilih"/></SelectTrigger>
                <SelectContent className="bg-white z-[9999]">
                  <SelectItem value="Ya">Ya (SDE)</SelectItem>
                  <SelectItem value="Tidak">Tidak (ASB)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            </div>

            {form.ppn_status === 'exclude' && (
              <div className="space-y-1.5 p-3 bg-amber-50 rounded-xl border border-amber-100">
                <Label className="font-bold uppercase text-[10px] text-amber-800">NPWP Perusahaan (Opsional untuk Faktur Pajak)</Label>
                <Input 
                  value={form.npwp} 
                  onChange={e => setForm({...form, npwp: e.target.value})} 
                  placeholder="Contoh: 01.234.567.8-012.000"
                  className="bg-white border-amber-200"
                />
              </div>
            )}

            <div className="pt-4 flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Batal</Button>
              <Button type="submit" className="bg-[#8C6208] text-white hover:bg-[#724F06]">
                Simpan Data
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
